import React, { useState, useEffect } from 'react';
import { Contact } from '@/types/crm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Mail, Phone, Calendar, TrendingUp, Star, Award, Gift, Ticket, CreditCard } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';

interface ContactDetailProps {
  contact: Contact;
  onClose: () => void;
}

interface Activity {
  id: string;
  type: 'review' | 'loyalty' | 'reward' | 'coupon' | 'loyalty_transaction';
  created_at: string;
  data: any;
}

const ContactDetail: React.FC<ContactDetailProps> = ({ contact, onClose }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, [contact.id]);

  const fetchActivities = async () => {
    try {
      setLoadingActivities(true);
      const allActivities: Activity[] = [];

      // Fetch reviews by email
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('id, rating, content, source, created_at, customer_name')
        .eq('customer_email', contact.email)
        .order('created_at', { ascending: false })
        .limit(25);

      if (reviewsError) throw reviewsError;

      if (reviews) {
        reviews.forEach((review) => {
          allActivities.push({
            id: review.id,
            type: 'review',
            created_at: review.created_at,
            data: {
              rating: review.rating,
              content: review.content,
              source: review.source,
              customer_name: review.customer_name,
            },
          });
        });
      }

      // Fetch loyalty passes by email
      const { data: passes, error: passesError } = await supabase
        .from('passes')
        .select(`
          id,
          created_at,
          installations_count,
          loyalty_program_id,
          loyalty_programs!inner(name, business_id)
        `)
        .eq('loyalty_programs.business_id', contact.business_id)
        .order('created_at', { ascending: false })
        .limit(25);

      if (passesError) {
        console.error('Error fetching passes:', passesError);
      } else if (passes) {
        // Filter passes that match this customer's email from pass_data
        for (const pass of passes) {
          // Check if pass_data contains email
          const { data: passData } = await supabase
            .from('passes')
            .select('pass_data')
            .eq('id', pass.id)
            .single();

          if (passData && passData.pass_data) {
            const email = passData.pass_data.email || passData.pass_data.customer_email;
            if (email && email.toLowerCase() === contact.email.toLowerCase()) {
              allActivities.push({
                id: pass.id,
                type: 'loyalty',
                created_at: pass.created_at,
                data: {
                  program_name: pass.loyalty_programs?.name,
                  installations: pass.installations_count,
                },
              });
            }
          }
        }
      }

      // Fetch reward emails sent
      const { data: rewards, error: rewardsError } = await supabase
        .from('rewarded_reviews')
        .select('id, review_id, sent_at, created_at')
        .eq('user_email', contact.email)
        .order('created_at', { ascending: false })
        .limit(25);

      if (rewardsError) {
        console.error('Error fetching rewards:', rewardsError);
      } else if (rewards) {
        rewards.forEach((reward) => {
          allActivities.push({
            id: reward.id,
            type: 'reward',
            created_at: reward.sent_at || reward.created_at,
            data: {
              review_id: reward.review_id,
            },
          });
        });
      }

      // Fetch coupon redemptions by customer_id (which might be email or UUID)
      const { data: coupons, error: couponsError } = await supabase
        .from('coupon_transactions')
        .select(`
          id,
          transaction_type,
          purchase_amount,
          discount_applied,
          created_at,
          coupon_offers(title)
        `)
        .eq('customer_id', contact.email)
        .order('created_at', { ascending: false })
        .limit(25);

      if (couponsError) {
        console.error('Error fetching coupons:', couponsError);
      } else if (coupons) {
        coupons.forEach((coupon) => {
          allActivities.push({
            id: coupon.id,
            type: 'coupon',
            created_at: coupon.created_at,
            data: {
              transaction_type: coupon.transaction_type,
              purchase_amount: coupon.purchase_amount,
              discount_applied: coupon.discount_applied,
              offer_title: coupon.coupon_offers?.title,
            },
          });
        });
      }

      // Fetch loyalty transactions by customer_id
      const { data: loyaltyTxns, error: loyaltyTxnsError } = await supabase
        .from('loyalty_transactions')
        .select(`
          id,
          transaction_type,
          points_earned,
          points_redeemed,
          stamps_earned,
          stamps_redeemed,
          reward_claimed,
          purchase_amount,
          created_at,
          loyalty_programs!inner(name, business_id)
        `)
        .eq('customer_id', contact.email)
        .eq('loyalty_programs.business_id', contact.business_id)
        .order('created_at', { ascending: false })
        .limit(25);

      if (loyaltyTxnsError) {
        console.error('Error fetching loyalty transactions:', loyaltyTxnsError);
      } else if (loyaltyTxns) {
        loyaltyTxns.forEach((txn) => {
          allActivities.push({
            id: txn.id,
            type: 'loyalty_transaction',
            created_at: txn.created_at,
            data: {
              transaction_type: txn.transaction_type,
              points_earned: txn.points_earned,
              points_redeemed: txn.points_redeemed,
              stamps_earned: txn.stamps_earned,
              stamps_redeemed: txn.stamps_redeemed,
              reward_claimed: txn.reward_claimed,
              purchase_amount: txn.purchase_amount,
              program_name: txn.loyalty_programs?.name,
            },
          });
        });
      }

      // Sort all activities by date
      allActivities.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setActivities(allActivities);
    } catch (error: any) {
      console.error('Error fetching activities:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const getInitials = () => {
    if (contact.first_name && contact.last_name) {
      return `${contact.first_name[0]}${contact.last_name[0]}`.toUpperCase();
    }
    return contact.email[0].toUpperCase();
  };

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'review':
        return <Star className="h-4 w-4 text-yellow-500" />;
      case 'loyalty':
        return <Award className="h-4 w-4 text-primary" />;
      case 'reward':
        return <Gift className="h-4 w-4 text-green-500" />;
      case 'coupon':
        return <Ticket className="h-4 w-4 text-orange-500" />;
      case 'loyalty_transaction':
        return <CreditCard className="h-4 w-4 text-blue-500" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const renderActivityContent = (activity: Activity) => {
    if (activity.type === 'review') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="font-medium">Review Submitted</div>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-3 w-3 ${
                    i < activity.data.rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
          {activity.data.content && (
            <div className="text-sm text-muted-foreground">
              "{activity.data.content.substring(0, 150)}
              {activity.data.content.length > 150 ? '...' : ''}"
            </div>
          )}
          {activity.data.source && (
            <Badge variant="outline" className="text-xs">
              {activity.data.source}
            </Badge>
          )}
        </div>
      );
    } else if (activity.type === 'loyalty') {
      return (
        <div className="space-y-2">
          <div className="font-medium">Joined Loyalty Program</div>
          <div className="text-sm text-muted-foreground">
            Program: {activity.data.program_name}
          </div>
          {activity.data.installations > 0 && (
            <Badge className="text-xs">
              {activity.data.installations} installation{activity.data.installations !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      );
    } else if (activity.type === 'reward') {
      return (
        <div className="space-y-2">
          <div className="font-medium">Reward Email Sent</div>
          <div className="text-sm text-muted-foreground">
            Received reward email for leaving a review
          </div>
          <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <Mail className="h-3 w-3 mr-1" />
            Email Delivered
          </Badge>
        </div>
      );
    } else if (activity.type === 'coupon') {
      return (
        <div className="space-y-2">
          <div className="font-medium">
            {activity.data.transaction_type === 'redemption' ? 'Coupon Redeemed' : 'Coupon Activity'}
          </div>
          {activity.data.offer_title && (
            <div className="text-sm text-muted-foreground">
              Offer: {activity.data.offer_title}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            {activity.data.purchase_amount && (
              <Badge variant="outline" className="text-xs">
                Purchase: ${Number(activity.data.purchase_amount).toFixed(2)}
              </Badge>
            )}
            {activity.data.discount_applied && (
              <Badge className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                Saved: ${Number(activity.data.discount_applied).toFixed(2)}
              </Badge>
            )}
          </div>
        </div>
      );
    } else if (activity.type === 'loyalty_transaction') {
      const isEarn = activity.data.points_earned > 0 || activity.data.stamps_earned > 0;
      const isRedeem = activity.data.points_redeemed > 0 || activity.data.stamps_redeemed > 0;

      return (
        <div className="space-y-2">
          <div className="font-medium">
            {isRedeem ? 'Reward Claimed' : 'Points Earned'}
          </div>
          <div className="text-sm text-muted-foreground">
            Program: {activity.data.program_name}
          </div>
          <div className="flex gap-2 flex-wrap">
            {activity.data.points_earned > 0 && (
              <Badge className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                +{activity.data.points_earned} points
              </Badge>
            )}
            {activity.data.stamps_earned > 0 && (
              <Badge className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                +{activity.data.stamps_earned} stamps
              </Badge>
            )}
            {activity.data.points_redeemed > 0 && (
              <Badge className="text-xs bg-primary/10 text-primary">
                -{activity.data.points_redeemed} points
              </Badge>
            )}
            {activity.data.stamps_redeemed > 0 && (
              <Badge className="text-xs bg-primary/10 text-primary">
                -{activity.data.stamps_redeemed} stamps
              </Badge>
            )}
            {activity.data.reward_claimed && (
              <Badge variant="default" className="text-xs">
                🎁 {activity.data.reward_claimed}
              </Badge>
            )}
            {activity.data.purchase_amount && (
              <Badge variant="outline" className="text-xs">
                ${Number(activity.data.purchase_amount).toFixed(2)}
              </Badge>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
      </div>

      {/* Contact Header Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-6">
            <Avatar className="h-20 w-20">
              <AvatarFallback className="text-2xl">{getInitials()}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-2xl font-bold">{contact.full_name || 'Unknown'}</h2>
              <div className="flex items-center gap-4 mt-2 text-muted-foreground">
                {contact.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>{contact.email}</span>
                  </div>
                )}
                {contact.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>{contact.phone}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Badge variant="default">{contact.lifecycle_stage}</Badge>
                <Badge variant="outline">{contact.lead_source}</Badge>
                {contact.tags?.map((tag) => (
                  <Badge
                    key={tag.tag_id}
                    variant="outline"
                    style={{ borderColor: tag.tag_color }}
                  >
                    {tag.tag_name}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Lead Score</div>
              <div className="text-3xl font-bold text-primary">{contact.lead_score}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabbed Content */}
      <Tabs defaultValue="activity" className="space-y-4">
        <TabsList>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>

        {/* Activity Timeline */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingActivities ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading activities...
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No activities yet
                </div>
              ) : (
                <div className="space-y-4">
                  {activities.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="p-2 bg-muted rounded-full">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1">
                        {renderActivityContent(activity)}
                        <div className="text-xs text-muted-foreground mt-2">
                          {new Date(activity.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Contact Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Email</div>
                  <div>{contact.email}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Phone</div>
                  <div>{contact.phone || 'Not provided'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Created</div>
                  <div>{new Date(contact.created_at).toLocaleDateString('en-GB').replace(/\//g, '.')}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Last Engagement</div>
                  <div>
                    {contact.last_engagement_at
                      ? new Date(contact.last_engagement_at).toLocaleDateString('en-GB').replace(/\//g, '.')
                      : 'Never'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Lead Source</div>
                  <div className="capitalize">{contact.lead_source || 'Unknown'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Lifecycle Stage</div>
                  <div className="capitalize">{contact.lifecycle_stage}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContactDetail;
